// Job photo upload/list/delete helpers. Wraps the private field-job-photos
// bucket + the field_job_photos metadata table so consumers don't repeat
// path construction, mime validation, or the upload-then-row-write
// ordering that keeps storage and DB in sync.
//
// Workspace isolation is enforced at TWO layers and we rely on both:
//   1. storage.objects RLS — gates the bucket by foldername(name)[1]::uuid
//      matching the caller's profiles.workspace_id.
//   2. field_job_photos RLS — gates the metadata row by workspace_id
//      matching get_user_workspace_id().
//
// Either layer alone would suffice on the happy path, but defense-in-depth
// keeps a future single-layer regression from silently leaking. The
// caller's workspaceId argument is used to BUILD the path; the policies
// (not the caller) are what actually enforce that it's correct.
//
// Mirrors the workspace-logos upload pattern from Settings.tsx:
// path → upload → row write → tolerate orphans on failure (vs. hard
// transactional rollback, which storage + Postgres can't do together).

import { supabase } from '@/lib/supabase'

const BUCKET = 'field-job-photos'

// Exported so consumer surfaces (UI input accept attributes, pre-upload
// validation, the NewJobModal staging picker) all derive from the same
// list and never drift from the bucket's allowed_mime_types.
export const ALLOWED_MIME = [
  'image/jpeg',
  'image/png',
  'image/webp',
  'image/heic',
] as const

/** Bucket-level size cap (10MB). Exported for client-side pre-upload
 *  rejection so an oversized file fails at pick time, not at storage. */
export const MAX_PHOTO_BYTES = 10 * 1024 * 1024

/** Comma-separated MIME list for HTML <input accept="..."> attributes. */
export const PHOTO_ACCEPT_ATTR = ALLOWED_MIME.join(',')

export interface JobPhoto {
  id: string
  workspace_id: string
  field_job_id: string
  storage_path: string
  caption: string | null
  uploaded_by: string | null
  // V2b column: when set, this photo was uploaded through the vendor
  // portal (staff path keeps uploaded_by populated and this NULL).
  // The two columns are mutually exclusive by app convention; both
  // NULL is possible for legacy rows that predated the V2b column.
  uploaded_by_vendor_user_id: string | null
  uploaded_at: string
  byte_size: number | null
}

export interface UploadJobPhotoInput {
  workspaceId: string
  jobId: string
  file: File
  caption?: string
}

export type UploadResult =
  | { data: JobPhoto; error: null }
  | { data: null; error: Error }

/**
 * Upload a photo, then INSERT the metadata row. On row-insert failure,
 * best-effort remove the just-uploaded object so storage doesn't drift
 * from the metadata table.
 */
export async function uploadJobPhoto({
  workspaceId, jobId, file, caption,
}: UploadJobPhotoInput): Promise<UploadResult> {
  if (!ALLOWED_MIME.includes(file.type as typeof ALLOWED_MIME[number])) {
    return { data: null, error: new Error(`Unsupported file type: ${file.type || 'unknown'}`) }
  }

  // Path = {workspace_id}/{field_job_id}/{uuid}.{ext}
  //   First segment is checked by storage RLS against the caller's
  //   workspace_id. If the caller forges a workspace_id they don't
  //   belong to, the policy rejects the upload — the argument is for
  //   convenience, the storage layer is the authority.
  const ext = (file.name.split('.').pop() || extFromMime(file.type) || 'bin').toLowerCase()
  const objectId = crypto.randomUUID()
  const path = `${workspaceId}/${jobId}/${objectId}.${ext}`

  const { error: upErr } = await supabase.storage.from(BUCKET).upload(path, file, {
    cacheControl: '3600',
    contentType: file.type || undefined,
  })
  if (upErr) return { data: null, error: new Error(`Upload failed: ${upErr.message}`) }

  const { data: userData } = await supabase.auth.getUser()
  const uploadedBy = userData?.user?.id ?? null

  const { data: row, error: dbErr } = await supabase
    .from('field_job_photos')
    .insert({
      workspace_id: workspaceId,
      field_job_id: jobId,
      storage_path: path,
      caption: caption ?? null,
      uploaded_by: uploadedBy,
      byte_size: file.size,
    })
    .select()
    .single()

  if (dbErr || !row) {
    // Best-effort orphan cleanup. Mirrors Settings logo-remove: if the
    // delete itself fails, the upload already happened — we surface the
    // DB error to the caller so they can decide whether to retry.
    await supabase.storage.from(BUCKET).remove([path]).catch(() => { /* swallow */ })
    return { data: null, error: new Error(dbErr?.message ?? 'Row insert failed') }
  }

  return { data: row as JobPhoto, error: null }
}

/**
 * Short-lived signed URL for displaying a photo. Default 5 minutes —
 * long enough for the calendar/Drawer to render thumbnails, short
 * enough that a stolen URL has limited usefulness. Callers can override
 * for batch / lightbox contexts.
 */
export async function getJobPhotoSignedUrl(
  storagePath: string,
  ttlSeconds = 300,
): Promise<{ url: string | null; error: Error | null }> {
  const { data, error } = await supabase.storage
    .from(BUCKET)
    .createSignedUrl(storagePath, ttlSeconds)
  if (error || !data?.signedUrl) {
    return { url: null, error: error ?? new Error('No signed URL returned') }
  }
  return { url: data.signedUrl, error: null }
}

/**
 * DB row delete first, then best-effort storage remove. If storage
 * delete fails after a successful row delete we accept the orphan —
 * mirrors the workspace-logos remove pattern and keeps the DB as the
 * source of truth.
 */
export async function deleteJobPhoto(photo: Pick<JobPhoto, 'id' | 'storage_path'>): Promise<{ error: Error | null }> {
  const { error: dbErr } = await supabase.from('field_job_photos').delete().eq('id', photo.id)
  if (dbErr) return { error: new Error(dbErr.message) }
  await supabase.storage.from(BUCKET).remove([photo.storage_path]).catch(() => { /* swallow — DB is truth */ })
  return { error: null }
}

/**
 * Photos for a job, newest first. RLS scopes the result to the caller's
 * workspace; an attacker passing another workspace's jobId would just
 * see zero rows (no leak).
 */
export async function listJobPhotos(jobId: string): Promise<{ data: JobPhoto[]; error: Error | null }> {
  const { data, error } = await supabase
    .from('field_job_photos')
    .select('*')
    .eq('field_job_id', jobId)
    .order('uploaded_at', { ascending: false })
  if (error) return { data: [], error: new Error(error.message) }
  return { data: (data ?? []) as JobPhoto[], error: null }
}

function extFromMime(mime: string): string | null {
  switch (mime) {
    case 'image/jpeg': return 'jpg'
    case 'image/png':  return 'png'
    case 'image/webp': return 'webp'
    case 'image/heic': return 'heic'
    default: return null
  }
}
